import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getRouteOrdersViewItemDetailed } from 'shared/const/router';
import { getPrice } from 'shared/lib/getPrice/getPrice';

import type { ItemSavingsResult } from 'entities/Order/lib/calculateOrderSavings/calculateOrderSavings';

const savingsGreen = '#2CB353';

const tooltipRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #E0E0E0',
} as const;

const tooltipTitleSx = {
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: '16px',
    color: 'rgba(0, 0, 0, 0.5)',
    marginBottom: '4px',
} as const;

const tooltipRowLabelSx = {
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '16px',
    color: '#000',
} as const;

const amountSx = {
    fontWeight: 600,
    color: '#000',
    fontSize: '13px',
    lineHeight: '16px',
} as const;

export interface PostSubmissionSavingsTooltipContentProps {
    itemSavingsResult: ItemSavingsResult;
}

/**
 * Post-submission savings tooltip: same breakdown as Best Price Selected
 * (Current, Average, Your savings) but without title or link.
 */
export function PostSubmissionSavingsTooltipContent({
    itemSavingsResult,
}: Readonly<PostSubmissionSavingsTooltipContentProps>) {
    const { t } = useTranslation();
    const {
        pricePaid,
        baselinePrice,
        totalQuantity,
        savings,
        comparableSupplierCount,
    } = itemSavingsResult;
    const averageTotal = baselinePrice * totalQuantity;

    return (
        <Box sx={{ minWidth: 220 }}>
            <Box sx={{ ...tooltipRowSx, paddingTop: '8px' }}>
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Purchase price')}
                </Typography>
                <Typography variant="body2" sx={amountSx}>
                    {getPrice(pricePaid)}
                </Typography>
            </Box>
            <Box
                sx={{ ...tooltipRowSx, flexDirection: 'column', width: '100%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography variant="body2" sx={tooltipRowLabelSx}>
                        {t('Average')}
                    </Typography>
                    <Typography variant="body2" sx={amountSx}>
                        {getPrice(averageTotal)}
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        width: '100%',
                        fontSize: '11px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        marginTop: '4px',
                    }}
                >
                    {t('Compared across {{count}} suppliers', {
                        count: comparableSupplierCount,
                    })}
                </Typography>
            </Box>
            <Box
                sx={{
                    ...tooltipRowSx,
                    borderBottom: 'none',
                    paddingBottom: '4px',
                }}
            >
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Your savings')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ ...amountSx, color: savingsGreen }}
                >
                    {getPrice(savings)}
                </Typography>
            </Box>
        </Box>
    );
}

export interface BestPriceSelectedTooltipContentProps {
    itemSavingsResult: ItemSavingsResult;
}

export function BestPriceSelectedTooltipContent({
    itemSavingsResult,
}: Readonly<BestPriceSelectedTooltipContentProps>) {
    const { t } = useTranslation();
    const {
        pricePaid,
        baselinePrice,
        totalQuantity,
        savings,
        comparableSupplierCount,
    } = itemSavingsResult;
    const averageTotal = baselinePrice * totalQuantity;

    return (
        <Box sx={{ minWidth: 220 }}>
            <Typography variant="subtitle2" sx={tooltipTitleSx}>
                {t('Best Price Selected')}
            </Typography>
            <Box sx={tooltipRowSx}>
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Current')}
                </Typography>
                <Typography variant="body2" sx={amountSx}>
                    {getPrice(pricePaid)}
                </Typography>
            </Box>
            <Box
                sx={{ ...tooltipRowSx, flexDirection: 'column', width: '100%' }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Typography variant="body2" sx={tooltipRowLabelSx}>
                        {t('Average')}
                    </Typography>
                    <Typography variant="body2" sx={amountSx}>
                        {getPrice(averageTotal)}
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        width: '100%',
                        fontSize: '11px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        marginTop: '4px',
                    }}
                >
                    {t('Compared across {{count}} suppliers', {
                        count: comparableSupplierCount,
                    })}
                </Typography>
            </Box>
            <Box
                sx={{
                    ...tooltipRowSx,
                    borderBottom: 'none',
                    paddingBottom: '4px',
                }}
            >
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Your savings')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ ...amountSx, color: savingsGreen }}
                >
                    {getPrice(savings)}
                </Typography>
            </Box>
        </Box>
    );
}

export interface BetterPriceAvailableTooltipContentProps {
    itemSavingsResult: ItemSavingsResult;
    orderId: string;
    itemId: string;
}

export function BetterPriceAvailableTooltipContent({
    itemSavingsResult,
    orderId,
    itemId,
}: Readonly<BetterPriceAvailableTooltipContentProps>) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pricePaid, bestAchievableTotal, potentialSavings } =
        itemSavingsResult;

    const handleEditSourcing = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(getRouteOrdersViewItemDetailed(orderId, itemId));
    };

    return (
        <Box sx={{ minWidth: 220 }}>
            <Typography variant="subtitle2" sx={tooltipTitleSx}>
                {t('Better Price Available')}
            </Typography>
            <Box sx={tooltipRowSx}>
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Current')}
                </Typography>
                <Typography variant="body2" sx={amountSx}>
                    {getPrice(pricePaid)}
                </Typography>
            </Box>
            <Box sx={tooltipRowSx}>
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Best')}
                </Typography>
                <Typography variant="body2" sx={amountSx}>
                    {getPrice(bestAchievableTotal)}
                </Typography>
            </Box>
            <Box sx={{ ...tooltipRowSx }}>
                <Typography variant="body2" sx={tooltipRowLabelSx}>
                    {t('Potential savings')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ ...amountSx, color: savingsGreen }}
                >
                    {getPrice(potentialSavings)}
                </Typography>
            </Box>
            <Box sx={{ padding: '16px 0 4px' }}>
                <Link
                    component="button"
                    variant="body2"
                    sx={{
                        color: '#3F6EB7',
                        fontWeight: 400,
                        fontSize: '13px',
                        lineHeight: '16px',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={handleEditSourcing}
                >
                    {t('Edit product sourcing')}
                </Link>
            </Box>
        </Box>
    );
}
